/**
 * 인증 관련 서비스
 */
import apiClient from '../utils/apiClient';

/**
 * 로그인 서비스
 * @param {string} userId - 사용자 아이디
 * @param {string} password - 비밀번호
 * @param {boolean} rememberMe - 로그인 상태 유지
 * @returns {Promise<Object>} 로그인 응답
 */
export const login = async (userId, password, rememberMe = false) => {
  try {
    const response = await apiClient.post('/Account/Login', {
      account: userId,
      password,
      rememberMe,
    });

    // 성공 시 토큰과 사용자 정보 저장
    if (response != null && response.data != null && response.data.accessToken) {
      localStorage.setItem('authToken', response.data.accessToken);
    }

    if (response != null && response.data != null) {
      localStorage.setItem('userInfo', JSON.stringify(response.data));
    }

    return response;
  } catch (error) {
    throw error;
  }
};

/**
 * 로그아웃 서비스
 */
export const logout = async () => {
  try {
    // 서버에 로그아웃 요청 (토큰 무효화)
    try {
      await apiClient.post('/Account/Logout');
    } catch (error) {
    }
  } finally {
    const authKeys = ['authToken', 'userInfo'];
    authKeys.forEach(key => {
      if (localStorage.getItem(key)) {
        localStorage.removeItem(key);
      }
    });
    
    // 혹시 모를 인증 관련 추가 키들 정리 (auth, user, token 관련)
    const allKeys = Object.keys(localStorage);
    const authRelatedKeys = allKeys.filter(key => 
      key.toLowerCase().includes('auth') || 
      key.toLowerCase().includes('user') || 
      key.toLowerCase().includes('token') ||
      key.toLowerCase().includes('login') ||
      key.toLowerCase().includes('session')
    );
    
    authRelatedKeys.forEach(key => {
      localStorage.removeItem(key);
    });
    
    // 세션 저장소도 정리
    try {
      const sessionKeys = Object.keys(sessionStorage);
      const authSessionKeys = sessionKeys.filter(key => 
        key.toLowerCase().includes('auth') || 
        key.toLowerCase().includes('user') || 
        key.toLowerCase().includes('token') ||
        key.toLowerCase().includes('login') ||
        key.toLowerCase().includes('session')
      );
      
      authSessionKeys.forEach(key => {
        sessionStorage.removeItem(key);
      });
    } catch (error) {
    }
    
  }
};

/**
 * 회원가입 서비스
 * @param {Object} signupData - 회원가입 데이터
 * @returns {Promise<Object>} 회원가입 응답
 */
export const signup = async (signupData) => {
  try {
    const response = await apiClient.post('/Account/Register', signupData);
    return response;
  } catch (error) {
    console.error('❌ 회원가입 실패:', error);
    throw error;
  }
};

/**
 * 비밀번호 찾기/재설정 요청
 * @param {string} email - 이메일 주소
 * @returns {Promise<Object>} 응답
 */
export const requestPasswordReset = async (email) => {
  try {
    const response = await apiClient.post('/Account/ForgotPassword', { email });
    return response;
  } catch (error) {
    console.error('❌ 비밀번호 재설정 요청 실패:', error);
    throw error;
  }
};

/**
 * 비밀번호 재설정
 * @param {string} token - 재설정 토큰
 * @param {string} newPassword - 새 비밀번호
 * @returns {Promise<Object>} 응답
 */
export const resetPassword = async (token, newPassword) => {
  try {
    const response = await apiClient.post('/Account/ResetPassword', {
      token,
      newPassword,
    });
    return response;
  } catch (error) {
    console.error('❌ 비밀번호 재설정 실패:', error);
    throw error;
  }
};

/**
 * 현재 사용자 정보 조회
 * @returns {Object|null} 사용자 정보
 */
export const getCurrentUser = () => {
  try {
    const userInfo = localStorage.getItem('userInfo');
    return userInfo ? JSON.parse(userInfo) : null;
  } catch (error) {
    console.error('❌ 사용자 정보 파싱 오류:', error);
    return null;
  }
};

/**
 * 인증 토큰 조회
 * @returns {string|null} 토큰
 */
export const getAuthToken = () => {
  return localStorage.getItem('authToken');
};

/**
 * 로그인 상태 확인
 * @returns {boolean} 로그인 여부
 */
export const isAuthenticated = () => {
  const token = getAuthToken();
  const user = getCurrentUser();
  return !!(token && user);
};

/**
 * 토큰 유효성 검사 (서버 확인)
 * @returns {Promise<boolean>} 토큰 유효성
 */
export const validateToken = async () => {
  try {
    if (!isAuthenticated()) {
      return false;
    }

    await apiClient.get('/Account/ValidateToken');
    return true;
  } catch (error) {
    console.warn('⚠️ 토큰 검증 실패, 로그아웃 처리:', error);
    await logout();
    return false;
  }
};

/**
 * 사용자 프로필 조회
 * @returns {Promise<Object>} 사용자 프로필
 */
export const getUserProfile = async () => {
  try {
    const response = await apiClient.get('/Account/Profile');
    
    // 최신 정보로 로컬 저장소 업데이트
    if (response.user) {
      localStorage.setItem('userInfo', JSON.stringify(response.user));
    }
    
    return response;
  } catch (error) {
    console.error('❌ 프로필 조회 실패:', error);
    throw error;
  }
};

/**
 * 사용자 프로필 업데이트
 * @param {Object} profileData - 업데이트할 프로필 데이터
 * @returns {Promise<Object>} 응답
 */
export const updateUserProfile = async (profileData) => {
  try {
    const response = await apiClient.put('/Account/Profile', profileData);
    
    // 성공 시 로컬 저장소 업데이트
    if (response.user) {
      localStorage.setItem('userInfo', JSON.stringify(response.user));
    }
    
    return response;
  } catch (error) {
    console.error('❌ 프로필 업데이트 실패:', error);
    throw error;
  }
};